import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingSpinner from './LoadingSpinner';
import TournamentBracket from './TournamentBracket';
import GameModeRulesModal from './GameModeRulesModal';
import groupService from '../services/GroupService';
import { debugError } from '../utils/debugUtils';

export default function TournamentTab({ groupId, isAdmin, t, onSnackbar }) {
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [autoRestart, setAutoRestart] = useState(false);
  const [starting, setStarting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [viewingId, setViewingId] = useState(null);

  const loadCurrent = useCallback(() => {
    setLoading(true);
    setError(false);
    groupService
      .getCurrentTournament(groupId)
      .then(setTournament)
      .catch((err) => {
        if (err.code === 8003) {
          setTournament(null);
        } else {
          debugError('[TournamentTab] Error loading tournament:', err);
          setError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  useEffect(() => {
    setViewingId(null);
    loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    if (viewingId == null) return;
    setLoading(true);
    groupService
      .getTournament(groupId, viewingId)
      .then(setTournament)
      .catch((err) => {
        debugError('[TournamentTab] Error loading tournament detail:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [groupId, viewingId]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const created = await groupService.startTournament(groupId, autoRestart);
      setTournament(created);
      setStartOpen(false);
      setViewingId(null);
    } catch (err) {
      onSnackbar({ open: true, message: t(`apiErrors.${err.code}`, t('common.error')), severity: 'error' });
    } finally {
      setStarting(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    groupService
      .listTournaments(groupId)
      .then((data) => setHistory(Array.isArray(data) ? data : []))
      .catch((err) => debugError('[TournamentTab] Error loading history:', err));
  };

  if (loading) return <LoadingSpinner />;

  const canStart = isAdmin && (!tournament || tournament.status === 'COMPLETED');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {viewingId != null && (
            <IconButton size="small" onClick={() => setViewingId(null)}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="subtitle1" fontWeight={700}>
            {t('groups.tournament.title')}
          </Typography>
          <IconButton size="small" onClick={() => setRulesOpen(true)} aria-label={t('groups.tournament.infoTooltip')}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <IconButton size="small" onClick={handleOpenHistory} aria-label={t('groups.tournament.historyButton')}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Box>

      {error ? (
        <Typography textAlign="center" color="error" py={2}>
          {t('common.error')}
        </Typography>
      ) : !tournament ? (
        <Typography textAlign="center" color="text.secondary" py={4}>
          {t('groups.tournament.noneActive')}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            color={tournament.status === 'ACTIVE' ? 'primary' : 'default'}
            label={t(tournament.status === 'ACTIVE' ? 'groups.tournament.statusActive' : 'groups.tournament.statusCompleted')}
            sx={{ mb: 1.5 }}
          />
          <TournamentBracket tournament={tournament} t={t} />
        </Box>
      )}

      {canStart && viewingId == null && (
        <Button variant="contained" onClick={() => setStartOpen(true)} sx={{ mt: 1 }}>
          {t('groups.tournament.startButton')}
        </Button>
      )}

      <Dialog open={startOpen} onClose={() => setStartOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <strong>{t('groups.tournament.startConfirmTitle')}</strong>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('groups.tournament.startConfirmMessage')}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={autoRestart} onChange={(e) => setAutoRestart(e.target.checked)} />}
            label={t('groups.tournament.autoRestartLabel')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartOpen(false)} disabled={starting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleStart} variant="contained" disabled={starting}>
            {t('groups.tournament.startButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <strong>{t('groups.tournament.historyTitle')}</strong>
        </DialogTitle>
        <DialogContent>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              {t('groups.tournament.historyEmpty')}
            </Typography>
          ) : (
            <List>
              {history.map((h) => (
                <ListItemButton
                  key={h.id}
                  onClick={() => {
                    setViewingId(h.id);
                    setHistoryOpen(false);
                  }}
                >
                  <ListItemText
                    primary={h.winnerName ? t('groups.tournament.winner', { name: h.winnerName }) : t(`groups.tournament.status${h.status === 'ACTIVE' ? 'Active' : 'Completed'}`)}
                    secondary={h.startedAt ? new Date(h.startedAt).toLocaleDateString() : ''}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <GameModeRulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} mode="tournament" t={t} />
    </Box>
  );
}
